// PRD §3.3 + §3.7 — Animal request/response schemas shared with mobile.
//
// `CreateAnimalRequestSchema` is the strict body shape accepted by
// `POST /api/v1/animals`. The client supplies the official ear-tag (crotal_id)
// and the production destination; the server resolves the owner's farm from
// the JWT user and assigns `qr_code` itself. Optional fields stay optional.
//
// `AnimalResponseSchema` mirrors `StoredAnimal` after serialisation (dates as
// ISO 8601 strings). Reused by GET /animals/:id, GET /animals, and the
// alternate-key lookups already shipping in M2.
import { z } from 'zod';
import { CROTAL_REGEX, validateSpanishCrotalId } from './crotal.js';
export const ANIMAL_DESTINATIONS = ['BEEF', 'DAIRY', 'BREEDING'];
export const CreateAnimalRequestSchema = z
    .object({
    crotal_id: z
        .string()
        .regex(CROTAL_REGEX, 'crotal_id must be ES + 13 digits')
        .refine((v) => validateSpanishCrotalId(v).valid, {
        message: 'crotal_id checksum (ISO 11784) is invalid',
    }),
    destination: z.enum(ANIMAL_DESTINATIONS),
    // Piloto §F1 (2026-05-13) — optional nickname.
    name: z.string().min(1).max(80).optional(),
    breed: z.string().min(1).max(80).optional(),
    sex: z.enum(['M', 'F']).optional(),
    birth_date: z.string().datetime({ offset: true }).optional(),
    leyrospot_uuid: z.string().uuid().optional(),
    // Lote V-7 Fase B — certificacións opcionais (raza pura, ecolóxico,
    // ENB, IGP Vaca galega, etc.). Renderizadas no PDF do contrato
    // sección 2.2. Cap 5 entradas por animal; sen formato esixido
    // máis que ser string non baleiro.
    certifications: z.array(z.string().min(1).max(80)).max(5).optional(),
})
    .strict();
// Phase 25 iter-17 (M10) — bulk import. A FARMER with 200+ animals can't
// onboard one at a time; this endpoint accepts a small batch (≤100 rows)
// and returns a per-row result so the client can render a "23 OK · 2 failed"
// summary with the rejected crotals listed for fixup. Above 100 rows the
// client splits into multiple batches.
export const ANIMAL_BATCH_MAX_ROWS = 100;
export const CreateAnimalBatchRequestSchema = z
    .object({
    animals: z.array(CreateAnimalRequestSchema).min(1).max(ANIMAL_BATCH_MAX_ROWS),
})
    .strict();
export const AnimalBatchResultSchema = z.object({
    /** 0-based row index in the request payload. Lets the client map a
     *  failure back to the original CSV line for the user. */
    index: z.number().int().nonnegative(),
    crotal_id: z.string(),
    status: z.enum(['CREATED', 'DUPLICATE', 'VALIDATION_ERROR', 'OTHER_ERROR']),
    /** Set when status === 'CREATED'. */
    animal_id: z.string().uuid().nullable(),
    /** Set when status !== 'CREATED' — human-readable, localised on the
     *  client. The backend ships an English key + optional context. */
    reason: z.string().nullable(),
});
export const CreateAnimalBatchResponseSchema = z.object({
    results: z.array(AnimalBatchResultSchema),
    created: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
});
export const AnimalResponseSchema = z.object({
    id: z.string().uuid(),
    crotal_id: z.string(),
    leyrospot_uuid: z.string().nullable(),
    qr_code: z.string(),
    farm_id: z.string().uuid(),
    species: z.string(),
    // Piloto §F1 (2026-05-13) — optional nickname.
    name: z.string().nullable(),
    breed: z.string().nullable(),
    sex: z.enum(['M', 'F']).nullable(),
    birth_date: z.string().datetime({ offset: true }).nullable(),
    photo_url: z.string().nullable(),
    destination: z.enum(ANIMAL_DESTINATIONS),
    current_owner_id: z.string().uuid().nullable(),
    current_hash: z.string().nullable(),
    // F-005 (Lote V-1) — replaces the iter-21 `pending_signature_count` which
    // expressed "events of others awaiting your signature" — a flow the
    // product team explicitly rejected (a VET only signs what they record
    // themselves). The new metric is the count of days remaining on the
    // animal's active medication/vaccine withdrawal period (the
    // `withdrawal_days` from the latest MEDICATION or VACCINE event with
    // that field set). `0` or `null` means the animal is clean — safe to
    // slaughter / move. Critical for VET + FARMER decisions.
    active_withdrawal_days_remaining: z.number().int().nonnegative().nullable().optional(),
    /** @deprecated use active_withdrawal_days_remaining. Always 0 on the wire
     *  from V-1 onward; remove the field after a mobile rotation. */
    pending_signature_count: z.number().int().nonnegative().nullable().optional(),
    // F-018 (Lote V-2) — timestamp when the animal arrived from a transfer
    // delivery. Mobile renders a "🆕 Novo" pill while NOW < this + 3 days.
    transported_in_at: z.string().datetime({ offset: true }).nullable().optional(),
    // Lote V-7 Fase B — certificacións do animal (raza pura, ENB, IGP).
    // Default `[]`; opcional para non romper respostas legacy.
    certifications: z.array(z.string()).default([]),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
});
// PRD §3.3 — pagination guards `GET /api/v1/animals` against unbounded list
// responses. Defaults to a single bounded page; clients can opt-in to follow
// `next_cursor` if a farm grows past `limit`.
export const ANIMAL_LIST_DEFAULT_LIMIT = 200;
export const ANIMAL_LIST_MAX_LIMIT = 500;
export const AnimalListQuerySchema = z
    .object({
    limit: z.coerce.number().int().min(1).max(ANIMAL_LIST_MAX_LIMIT).optional(),
    cursor: z.string().uuid().optional(),
})
    .strict();
export const AnimalListResponseSchema = z.object({
    animals: z.array(AnimalResponseSchema),
    next_cursor: z.string().uuid().nullable().default(null),
});
// PRD §3.2 + audit phase 10.4 — associate NFC tag UUID to an animal.
export const AssociateLeyrospotRequestSchema = z
    .object({
    leyrospot_uuid: z.string().uuid(),
})
    .strict();
// Phase 25 iter-29 (Lote V, G1) — three-generation pedigree response.
// Each slot is either a hydrated reference (the animal exists in LeyroChain
// and we resolved it via `BIRTH.mother_crotal` / `BIRTH.father_crotal`) or
// null (no BIRTH event in the system OR the crotal in the BIRTH payload
// doesn't match any animal we know — common for animals bought from
// outside the platform).
//
// The 4 grandparents are optional; the backend resolves them only when
// the mother/father themselves have a BIRTH event with crotal pointers.
// Two-generation pedigrees stay valid for the common piloto case where
// only the immediate parents are recorded.
export const PedigreeNodeSchema = z.object({
    animal_id: z.string().uuid().nullable(),
    crotal_id: z.string().nullable(),
    name: z.string().nullable(),
    breed: z.string().nullable(),
});
export const PedigreeResponseSchema = z.object({
    subject: PedigreeNodeSchema,
    mother: PedigreeNodeSchema.nullable(),
    father: PedigreeNodeSchema.nullable(),
    maternal_grandmother: PedigreeNodeSchema.nullable(),
    maternal_grandfather: PedigreeNodeSchema.nullable(),
    paternal_grandmother: PedigreeNodeSchema.nullable(),
    paternal_grandfather: PedigreeNodeSchema.nullable(),
});
//# sourceMappingURL=animal.js.map